import React, { useEffect } from "react";
import { connect } from "react-redux";
import { message, Form } from "antd";
import Layout from "components/layout";
import { RegisterButton } from "components/registerButton";
import { bindActionCreators } from "redux";
import { Creators as DocumentActions } from "store/document/document-duck";
import { IEditDocument, IItems } from "store/document/document-model";
import { Creators as UserSessionActions } from "store/user-session/user-session-duck";
import { IUserData, MenuItemActive } from "store/user-session/user-session-model";
import TokenService from "utils/auth/Token.service";
import useTranslation from "utils/hooks/useTranslation";
import YmsDrawer from "./../../../components/drawer";
import FormNewDocument from "./components/form";
import { onConfirmDrawerNewDocument, onConfirmDrawerUpdateDocument } from "./document.service";
import DocumentFilter from "./filter";
import DocumentGrid from "./grid";
import * as S from "./styled/index.styled";

interface IPropsScreenDocument {
  isLoading?: boolean;
  isLoadingTable?: boolean;
  items?: Array<IItems>;
  totalItems?: number;
  pageIndex?: number;
  searchFilter?: string;
  maskAllowLetters?: boolean;
  maskAllowNumbers?: boolean;
  maskAllowSpecialCharacters?: boolean;
  maskMinCharacters?: number;
  maskMaxCharacters?: number;
  visibleDrawerNewDocument?: boolean;
  disabledButton?: boolean;
  editDocument?: IEditDocument;
  formEditModified?: boolean;
  visibleDrawerEditDocument?: boolean;
  documentId?: number;
  userData?: IUserData;
}
interface IDispatchProps {
  documentFetchAllActivesAsync?: (...args) => void;
  documentChangeFieldValue?: (...args) => void;
  documentShowDrawerNewDocument?: (...args) => void;
  documentHideDrawerNewDocument?: (...args) => void;
  documentSaveNewDocumentAsync?: (...args) => void;
  documentUpdateFilter?: (...args) => void;
  documentDeleteByIdAsync?: (...args) => void;
  documentMaskAllowLetterActive?: (...args) => void;
  documentMaskAllowNumberActive?: (...args) => void;
  documentMaskAllowSpecialCharactersActive?: (...args) => void;
  documentMaskMinCharactersActive?: (...args) => void;
  documentMaskMaxCharactersActive?: (...args) => void;
  documentClearMaskDocuments?: (...args) => void;
  documentUpdateFormModified?: (...args) => void;
  documentHideDrawerEditDocument?: (...args) => void;
  documentShowDrawerEditDocument?: (...args) => void;
  documentUpdateDocument?: (...args) => void;
  documentUpdateDocumentAsync?: (...args) => void;
  documentFetchByIdAsync?: (...args) => void;
  documentIdActive?: (...args) => void;
  userSessionMenuItemActive?: (...args) => void;
}

type IProps = IPropsScreenDocument & IDispatchProps;

const Document = ({
  isLoading,
  isLoadingTable,
  items,
  totalItems,
  pageIndex,
  userData,
  visibleDrawerNewDocument,
  disabledButton,
  editDocument,
  documentId,
  searchFilter,
  maskAllowLetters,
  maskAllowNumbers,
  maskAllowSpecialCharacters,
  maskMinCharacters,
  maskMaxCharacters,
  formEditModified,
  visibleDrawerEditDocument,
  documentUpdateDocument,
  documentHideDrawerEditDocument,
  documentShowDrawerEditDocument,
  documentFetchAllActivesAsync,
  documentChangeFieldValue,
  documentShowDrawerNewDocument,
  documentHideDrawerNewDocument,
  documentSaveNewDocumentAsync,
  documentUpdateFilter,
  documentDeleteByIdAsync,
  documentMaskAllowLetterActive,
  documentMaskAllowNumberActive,
  documentMaskAllowSpecialCharactersActive,
  documentMaskMinCharactersActive,
  documentMaskMaxCharactersActive,
  documentClearMaskDocuments,
  documentUpdateFormModified,
  documentUpdateDocumentAsync,
  documentFetchByIdAsync,
  userSessionMenuItemActive,
  documentIdActive,
}: IProps) => {
  const tokenService = new TokenService();
  const TITLE_PAGE = useTranslation({ id: "document" });
  const REQUIRED_FIELDS_MESSAGE = useTranslation({ id: "required_fields_are_required" });
  const [form] = Form.useForm();
  const [formEdit] = Form.useForm();

  useEffect(() => {
    userSessionMenuItemActive(MenuItemActive.DOCUMENTS);
    tokenService.authenticateToken();
  }, []);

  const handleOnConfirmDrawerNewDocument = () =>
    onConfirmDrawerNewDocument({
      form,
      maskAllowLetters,
      maskAllowNumbers,
      maskAllowSpecialCharacters,
      maskMinCharacters,
      maskMaxCharacters,
      documentSaveNewDocumentAsync,
      message,
      messageDescription: REQUIRED_FIELDS_MESSAGE,
    });

  const handleOnConfirmDrawerUpdateDocument = () => {
    onConfirmDrawerUpdateDocument({
      formEdit,
      documentId,
      maskAllowLetters,
      maskAllowNumbers,
      maskAllowSpecialCharacters,
      maskMinCharacters,
      maskMaxCharacters,
      documentUpdateDocumentAsync,
      message,
      messageDescription: REQUIRED_FIELDS_MESSAGE,
    });
  };

  useEffect(() => {
    if (!visibleDrawerNewDocument) {
      documentClearMaskDocuments();
      form.resetFields();
    }
  }, [visibleDrawerNewDocument]);

  useEffect(() => {
    if (!visibleDrawerEditDocument) {
      formEdit.resetFields();
      documentUpdateDocument(null);
      documentClearMaskDocuments();
      documentUpdateFormModified(false);
    }
  }, [visibleDrawerEditDocument]);

  useEffect(() => {
    return () => {
      documentUpdateFilter(null);
    };
  }, []);

  return (
    <Layout title={TITLE_PAGE} isLoading={isLoading} role={userData.role}>
      <S.DefaultContainer>
        <S.HeaderContainer>
          <RegisterButton id="create-document" title="new_document" dataTestId="createDocument" onClick={documentShowDrawerNewDocument} />
          <DocumentFilter documentUpdateFilter={documentUpdateFilter} documentChangeFieldValue={documentChangeFieldValue} />
        </S.HeaderContainer>
        <YmsDrawer
          width="680"
          confirmbuttontxt={useTranslation({ id: "save" })}
          cancelbuttontxt={useTranslation({ id: "cancel" })}
          maskClosable={true}
          disabled={disabledButton}
          onConfirm={handleOnConfirmDrawerNewDocument}
          title={useTranslation({ id: "register_document" })}
          placement="right"
          closable={false}
          destroyOnClose={true}
          onClose={documentHideDrawerNewDocument}
          open={visibleDrawerNewDocument}
        >
          <FormNewDocument
            form={form}
            documentMaskAllowLetterActive={documentMaskAllowLetterActive}
            documentMaskAllowNumberActive={documentMaskAllowNumberActive}
            documentMaskAllowSpecialCharactersActive={documentMaskAllowSpecialCharactersActive}
            documentMaskMinCharactersActive={documentMaskMinCharactersActive}
            documentMaskMaxCharactersActive={documentMaskMaxCharactersActive}
            maskAllowLetters={maskAllowLetters}
            maskAllowNumbers={maskAllowNumbers}
            maskAllowSpecialCharacters={maskAllowSpecialCharacters}
            maskMinCharacters={maskMinCharacters}
            maskMaxCharacters={maskMaxCharacters}
          />
        </YmsDrawer>
        <DocumentGrid
          form={formEdit}
          items={items}
          totalItems={totalItems}
          documentChangeFieldValue={documentChangeFieldValue}
          documentFetchAllActivesAsync={documentFetchAllActivesAsync}
          documentDeleteByIdAsync={documentDeleteByIdAsync}
          formEditModified={formEditModified}
          editDocument={editDocument}
          pageIndex={pageIndex}
          searchFilter={searchFilter}
          documentFetchByIdAsync={documentFetchByIdAsync}
          visibleDrawerEditDocument={visibleDrawerEditDocument}
          documentHideDrawerEditDocument={documentHideDrawerEditDocument}
          documentShowDrawerEditDocument={documentShowDrawerEditDocument}
          documentMaskAllowLetterActive={documentMaskAllowLetterActive}
          documentMaskAllowNumberActive={documentMaskAllowNumberActive}
          handleOnConfirmDrawerUpdateDocument={handleOnConfirmDrawerUpdateDocument}
          documentMaskAllowSpecialCharactersActive={documentMaskAllowSpecialCharactersActive}
          documentMaskMinCharactersActive={documentMaskMinCharactersActive}
          documentMaskMaxCharactersActive={documentMaskMaxCharactersActive}
          documentUpdateFormModified={documentUpdateFormModified}
          maskAllowLetters={maskAllowLetters}
          maskAllowNumbers={maskAllowNumbers}
          maskAllowSpecialCharacters={maskAllowSpecialCharacters}
          maskMinCharacters={maskMinCharacters}
          maskMaxCharacters={maskMaxCharacters}
          documentIdActive={documentIdActive}
          isLoadingTable={isLoadingTable}
        />
      </S.DefaultContainer>
    </Layout>
  );
};

const mapDispatchToActions = (dispatch: any) => bindActionCreators({ ...DocumentActions, ...UserSessionActions }, dispatch);

const mapStateToProps = (state) => ({
  items: state.Document.items,
  userData: state.UserSession.userData,
  isLoading: state.Document.isLoading,
  totalItems: state.Document.totalItems,
  searchFilter: state.Document.searchFilter,
  pageIndex: state.Document.filter.pageIndex,
  disabledButton: state.Document.disabledButton,
  visibleDrawerNewDocument: state.Document.visibleDrawerNewDocument,
  maskAllowLetters: state.Document.maskAllowLetters,
  maskAllowNumbers: state.Document.maskAllowNumbers,
  maskAllowSpecialCharacters: state.Document.maskAllowSpecialCharacters,
  maskMinCharacters: state.Document.maskMinCharacters,
  maskMaxCharacters: state.Document.maskMaxCharacters,
  editDocument: state.Document.editDocument,
  formEditModified: state.Document.formEditModified,
  visibleDrawerEditDocument: state.Document.visibleDrawerEditDocument,
  documentId: state.Document.documentId,
  isLoadingTable: state.Document.isLoadingTable,
});

export default connect(mapStateToProps, mapDispatchToActions)(Document);
